import { alertController, loadingController, toastController } from '@ionic/core';
import { Component, Event, forceUpdate, Fragment, h, Prop, State } from '@stencil/core';
import { PersonDoc, ProjectDoc, RFISettingsDoc, Workflow, WorkflowState } from 'common';
import { PeopleService } from 'src/global/service/PeopleService';
import { User } from '../../global/models';
import { ProjectService } from '../../projects/services/ProjectService';
import { ConcourseDb } from 'src/global/ConcourseDb';
import { RfiService } from 'src/RFIs/services/RfiService';
import { WorkflowEditorModal } from '../../submittals/components/workflow-editor-modal';
import { ConcoursePersonSelector } from '../../components/concourse-person-selector/concourse-person-selector';
import { RfiUtils } from '..';

@Component({
  tag: 'rfi-settings',
  styleUrl: 'concourse-rfis.css',
  //shadow: true,
})
export class RfiSettings {
  @Prop() projectId;
  @State() project: ProjectDoc;
  @State() people: PersonDoc[];
  @State() settings: RFISettingsDoc = { states: [], fields: [] };
  @State() settingsError: string;

  @State() editingState: WorkflowState;
  @State() editingStateError: string;

  @Event({ bubbles: true }) requestAccess;
  get user(): User {
    return globalThis.user;
  }

  async componentDidLoad() {
    console.log('RfiSettings.componentDidLoad', this.projectId);
    if (!this.user.canAdministerProject) {
      this.requestAccess.emit({ title: 'project Admin', role: `p-${this.projectId}-write` });
      return;
    }

    const loadingModal = await loadingController.create({ message: 'Loading Project...' });
    loadingModal.present();

    this.project = await ProjectService.getProjectDoc(this.projectId);

    loadingModal.message = 'Loading People...';
    this.people = await PeopleService.getPersonDocs(this.projectId);
    loadingModal.message = 'Loading RFI Settings...';
    this.settings = await RfiService.getRfiSettings(this.projectId);
    loadingModal.dismiss();
  }

  async onPeopleLoadComplete(people: PersonDoc[]) {
    this.people = people;
  }

  hasField(name: string) {
    return this.settings.fields.includes(name);
  }

  setField(name: string, checked: boolean) {
    if (checked) {
      this.settings.fields.push(name);
    } else {
      this.settings.fields = this.settings.fields.filter(field => field != name);
    }
  }

  addRfiState() {
    this.editingState = { id: ConcourseDb.makeShortDocId(), action: '', pastTense: '', dueDays: 5, dueBy: [] };
  }

  editRfiState(state: WorkflowState) {
    this.editingState = { ...state };
  }

  async showPersonSelector(state: WorkflowState) {
    const people = await ConcoursePersonSelector.present({ projectId: this.projectId, feature: 'rfis', peopleToOmitIds: state.dueBy, needsWrite: true });
    if (people && people.length > 0) {
      state.dueBy = [...state.dueBy, ...people.map(p => p._id)];
      forceUpdate(this);
    }
  }

  removePerson(state: WorkflowState, id: string) {
    state.dueBy = state.dueBy.filter(personId => personId !== id);
    forceUpdate(this);
  }

  async saveState() {
    this.editingStateError = this.getValidationErrors(this.editingState);
    if (this.editingStateError) return;

    const stateIndex = this.settings.states.findIndex(state => state.id == this.editingState.id);
    if (stateIndex > -1) {
      this.settings.states[stateIndex] = this.editingState;
    } else {
      this.settings.states.push(this.editingState);
    }

    this.editingState = null;
  }

  getValidationErrors(state: WorkflowState) {
    if (!state.action) {
      return 'Action of state is required';
    }
    if (!state.dueDays || state.dueDays < 1) {
      return 'Number of due days must be greater than zero';
    }
  }

  async deleteRfiState() {
    const stateIndex = this.settings.states.findIndex(state => state.id == this.editingState.id);
    if (stateIndex == this.settings.states.length - 1) {
      await alertController
        .create({
          header: 'Cannot Delete',
          message: 'You cannot delete the last status',
          buttons: ['OK'],
        })
        .then(alert => alert.present());
      return;
    }

    await alertController
      .create({
        header: 'Are you sure',
        message: 'If you delete this state, the RFIs that are in this state will be moved to the next state. If there is no next state, the rfis will not be visible',
        buttons: [
          {
            text: 'Cancel',
            role: 'cancel',
          },
          {
            text: 'Confirm',
            role: 'confirm',
            handler: async () => {
              this.settings.states = this.settings.states.filter(state => state.id != this.editingState.id);
              this.settings = await RfiService.saveSettings(this.projectId, this.settings);
              this.editingState = null;
              await toastController
                .create({
                  message: 'The state has been deleted',
                  duration: 5000,
                })
                .then(toast => toast.present());
            },
          },
        ],
      })
      .then(alert => alert.present());
  }

  async saveSettings() {
    this.settings = await RfiService.saveSettings(this.projectId, this.settings);
    toastController
      .create({
        message: 'The settings have been saved',
        duration: 5000,
      })
      .then(toast => toast.present());
  }

  navigateToRfis() {
    location.hash = `/project/${this.projectId}/rfis`;
  }

  async duplicateDefaultWorkflow() {
    this.editWorkflow({ id: ConcourseDb.makeShortDocId(), name: `New Workflow`, states: [...RfiUtils.DEFAULT_RFI_WORKFLOW.states] });
  }

  async editWorkflow(workflow: Workflow) {
    const edited = await WorkflowEditorModal.present(this.projectId, workflow, 'rfis');
    const workflows = this.project.rfis_workflows || [];
    if (edited && (edited as Workflow).id) {
      const idx = workflows.findIndex(w => w.id == (edited as Workflow).id);
      if (idx > -1) {
        workflows[idx] = edited as Workflow;
      } else {
        workflows.push(edited as Workflow);
      }
      this.project = await ProjectService.saveProjectDoc({ ...this.project, rfis_workflows: workflows });
    } else if (edited == 'delete') {
      this.project = await ProjectService.saveProjectDoc({ ...this.project, rfis_workflows: workflows.filter(w => w.id != workflow.id) });
    }
    forceUpdate(this);
  }

  render() {
    const workflows = [...(this.project?.rfis_workflows || [])];
    workflows.unshift(RfiUtils.DEFAULT_RFI_WORKFLOW);

    return (
      <Fragment>
        <concourse-header projectId={this.projectId} pageTitle="Rfi Settings" showBack={true} onBack={() => this.navigateToRfis()}></concourse-header>
        <ion-content>
          <ion-grid>
            <ion-row>
              <ion-col size-md="8" size-xs="12">
                {/* Workflows Section */}
                <ion-card>
                  <ion-card-header>
                    <ion-card-title>Workflows</ion-card-title>
                  </ion-card-header>
                  <ion-card-content>
                    <ion-list>
                      {workflows.map(workflow => (
                        <ion-item>
                          <ion-label>{workflow.name}</ion-label>
                          {workflow.id != RfiUtils.DEFAULT_RFI_WORKFLOW.id && (
                            <ion-button slot="end" fill="clear" onClick={() => this.editWorkflow(workflow)}>
                              <ion-icon slot="icon-only" name="create"></ion-icon>
                            </ion-button>
                          )}
                        </ion-item>
                      ))}
                    </ion-list>
                    <ion-button size="small" fill="clear" class="link-button" onClick={() => this.duplicateDefaultWorkflow()}>
                      <ion-icon slot="start" name="add-outline"></ion-icon>
                      New Workflow
                    </ion-button>
                  </ion-card-content>
                </ion-card>
              </ion-col>
            </ion-row>
            <ion-row>
              <ion-col size-md="6" size-xs="12">
                <ion-card>
                  <ion-card-header>
                    <ion-card-title>Fields</ion-card-title>
                  </ion-card-header>
                  <ion-card-content>
                    <ion-list>
                      <ion-item>
                        <ion-checkbox justify="space-between" checked="true" disabled="true">
                          * Subject
                        </ion-checkbox>
                      </ion-item>
                      <ion-item>
                        <ion-checkbox justify="space-between" checked="true" disabled="true">
                          * Description
                        </ion-checkbox>
                      </ion-item>
                      <ion-item>
                        <ion-checkbox justify="space-between" checked={this.hasField('question')} onIonChange={e => this.setField('question', e.detail.checked)}>
                          Question
                        </ion-checkbox>
                      </ion-item>
                      <ion-item>
                        <ion-checkbox justify="space-between" checked={this.hasField('suggestedAnswer')} onIonChange={e => this.setField('suggestedAnswer', e.detail.checked)}>
                          Suggested Answer
                        </ion-checkbox>
                      </ion-item>
                      <ion-item>
                        <ion-checkbox justify="space-between" checked="true" disabled="true">
                          * Due Date
                        </ion-checkbox>
                      </ion-item>
                      <ion-item>
                        <ion-checkbox justify="space-between" checked="true" disabled="true">
                          * Assigned To
                        </ion-checkbox>
                      </ion-item>
                      <ion-item>
                        <ion-checkbox justify="space-between" checked={this.hasField('distribution')} onIonChange={e => this.setField('distribution', e.detail.checked)}>
                          Distribution
                        </ion-checkbox>
                      </ion-item>
                      <ion-item>
                        <ion-checkbox justify="space-between" checked={this.hasField('tasks')} onIonChange={e => this.setField('tasks', e.detail.checked)}>
                          Tasks
                        </ion-checkbox>
                      </ion-item>
                      <ion-item>
                        <ion-checkbox justify="space-between" checked={this.hasField('tags')} onIonChange={e => this.setField('tags', e.detail.checked)}>
                          Tags
                        </ion-checkbox>
                      </ion-item>
                      <ion-item>
                        <ion-checkbox justify="space-between" checked={this.hasField('attachments')} onIonChange={e => this.setField('attachments', e.detail.checked)}>
                          Attachments
                        </ion-checkbox>
                      </ion-item>
                      <ion-item>
                        <ion-checkbox justify="space-between" checked={this.hasField('priority')} onIonChange={e => this.setField('priority', e.detail.checked)}>
                          Priority
                        </ion-checkbox>
                      </ion-item>
                      <ion-item>
                        <ion-checkbox justify="space-between" checked={this.hasField('cost-code')} onIonChange={e => this.setField('cost-code', e.detail.checked)}>
                          Cost Code
                        </ion-checkbox>
                      </ion-item>
                      <ion-item>
                        <ion-checkbox justify="space-between" checked={this.hasField('cost-impact')} onIonChange={e => this.setField('cost-impact', e.detail.checked)}>
                          Cost Impact
                        </ion-checkbox>
                      </ion-item>
                      <ion-item>
                        <ion-checkbox justify="space-between" checked={this.hasField('schedule-impact')} onIonChange={e => this.setField('schedule-impact', e.detail.checked)}>
                          Schedule Impact
                        </ion-checkbox>
                      </ion-item>
                    </ion-list>
                  </ion-card-content>
                </ion-card>
              </ion-col>
              <ion-col size-md="6" size-xs="12">
                <ion-card>
                  <ion-card-header>
                    <ion-card-title>Statuses</ion-card-title>
                  </ion-card-header>
                  <ion-card-content>
                    <ion-list>
                      {this.settings.states.map(state => (
                        <ion-item>
                          <ion-title>{state.action}</ion-title>
                          <ion-label>
                            '
                            {state.dueBy.map(id => (
                              <ion-chip class="chip">
                                <ion-avatar>
                                  <img src={PeopleService.getIconFromId(this.people, id)} />
                                </ion-avatar>
                                <ion-label>{PeopleService.getNameFromId(this.people, id)}</ion-label>
                              </ion-chip>
                            ))}
                            ' must '{state.action}' before {state.dueDays} days have passed
                          </ion-label>
                          <ion-button fill="clear" onClick={() => this.editRfiState(state)}>
                            <ion-icon name="create-outline"></ion-icon>
                          </ion-button>
                        </ion-item>
                      ))}
                    </ion-list>
                    <ion-toolbar>
                      <ion-button slot="end" fill="outline" onClick={() => this.addRfiState()}>
                        Add Status
                      </ion-button>
                    </ion-toolbar>
                  </ion-card-content>
                </ion-card>
              </ion-col>
            </ion-row>
          </ion-grid>
        </ion-content>
        <ion-footer class="ion-padding-horizontal">
          {this.settingsError && <ion-text color="danger">{this.settingsError}</ion-text>}
          <ion-toolbar>
            <ion-button slot="end" fill="solid" color="primary" onClick={() => this.saveSettings()}>
              Save
            </ion-button>
          </ion-toolbar>
        </ion-footer>

        {this.editingState && (
          <ion-modal isOpen={!!this.editingState} class="modal-height-auto">
            <ion-header class="search-modal-header ion-no-border">
              <ion-toolbar>
                <ion-title>{this.editingState.action ? `Edit '${this.editingState.action}' Status` : 'Create New Status'}</ion-title>
                <ion-buttons slot="end">
                  <ion-button class="search-modal-close" onclick={() => (this.editingState = null)}>
                    <ion-icon name="close" />
                  </ion-button>
                </ion-buttons>
              </ion-toolbar>
              <ion-page class="ion-padding">
                <ion-input placeholder="Status" value={this.editingState.action} onIonChange={e => (this.editingState.action = e.target.value)}></ion-input>
                <ion-input
                  placeholder="Number of Days to complete Action"
                  value={this.editingState.dueDays}
                  onIonChange={e => (this.editingState.dueDays = Number(e.target.value))}
                ></ion-input>
                <ion-label>Persons responsable: </ion-label>
                <span>
                  {this.editingState.dueBy.map(id => (
                    <ion-chip class="chip">
                      <ion-avatar>
                        <img src={PeopleService.getIconFromId(this.people, id)} />
                      </ion-avatar>
                      <ion-label>{PeopleService.getNameFromId(this.people, id)}</ion-label>
                      <ion-icon name="close-circle" onClick={() => this.removePerson(this.editingState, id)}></ion-icon>
                    </ion-chip>
                  ))}
                </span>
                <ion-button fill="clear" onClick={() => this.showPersonSelector(this.editingState)}>
                  <ion-icon slot="icon-only" name="add-circle-outline"></ion-icon>
                </ion-button>
              </ion-page>
              <ion-footer>
                {this.editingStateError && <ion-text color="danger">{this.editingStateError}</ion-text>}
                <ion-toolbar>
                  <ion-button slot="start" fill="clear" onClick={() => this.deleteRfiState()}>
                    <ion-icon name="trash-outline"></ion-icon>
                  </ion-button>
                  <ion-buttons slot="end">
                    <ion-button onClick={() => (this.editingState = null)}>Cancel</ion-button>
                    <ion-button onClick={() => this.saveState()}>Save</ion-button>
                  </ion-buttons>
                </ion-toolbar>
              </ion-footer>
            </ion-header>
          </ion-modal>
        )}
      </Fragment>
    );
  }
}
